import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import JoinNowFormPopup from "../../../componants/JoinNowFormPopup";

// Helper component for Star Rating
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-400" : "text-gray-300"
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Helper component for Verified Badge
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2">
    <svg
      className="w-3 h-3 mr-1"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

// Helper component for Google Badge (assuming it's present in the original design and I'm adding it)
const GoogleBadge = () => (
    <span className="ml-2">
        <img src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" alt="Google" className="h-4 w-4 inline-block" />
    </span>
);


const CPlusCourseInNoida = () => {

  const [submitMessage, setSubmitMessage] = useState<string | null>(null);


  const testimonials = [
    {
      id: 1,
      name: "Rohit Agrawal",
      review:
        "The C/C++ course at Nadezhda Venture is phenomenal! I mastered DSA and competitive programming concepts which are crucial for interviews. The instructors in Noida are highly skilled and provide excellent one-on-one support. Best place to build strong programming fundamentals.",
      rating: 5,
      date: "2024-07-30",
      profileImg: "https://randomuser.me/api/portraits/men/34.jpg", // Placeholder image
    },
    {
      id: 2,
      name: "Manisha Devi",
      review:
        "Nadezhda Venture's C++ certification program truly enhanced my problem-solving skills. The in-depth coverage of OOP and data structures prepared me for complex coding challenges. The practical assignments were key to understanding real-world applications.",
      rating: 4,
      date: "2024-07-27",
      profileImg: "https://randomuser.me/api/portraits/women/48.jpg", // Placeholder image
    },
    {
      id: 3,
      name: "Vikram Saini",
      review:
        "For anyone serious about system programming or game development, this C/C++ course is a must. The faculty explained pointers and memory management exceptionally well. Plus, the placement assistance in Noida is very helpful. Great learning environment!",
      rating: 5,
      date: "2024-07-25",
      profileImg: "https://randomuser.me/api/portraits/men/53.jpg", // Placeholder image
    },
    {
      id: 4,
      name: "Anjali Verma",
      review:
        "I came to Nadezhda Venture to strengthen my C/C++ basics for competitive programming, and it exceeded my expectations. The focus on algorithms and performance optimization was invaluable. I highly recommend this institute in Noida for its quality education.",
      rating: 4,
      date: "2024-07-22",
      profileImg: "https://randomuser.me/api/portraits/women/65.jpg", // Placeholder image
    },
    {
      id: 5,
      name: "Saurabh Kumar",
      review:
        "Excellent course structure and very supportive teachers. I learned C and C++ from scratch and now feel confident tackling DSA problems. The live projects helped immensely in applying theoretical knowledge. Thank you, Nadezhda Venture!",
      rating: 5,
      date: "2024-07-20",
      profileImg: "https://randomuser.me/api/portraits/men/80.jpg", // Placeholder image
    },
  ];

   const [isPopupOpen, setIsPopupOpen] = useState(false); // State to control popup visibility
      const [selectedCourseTitle, setSelectedCourseTitle] = useState(""); // State to store the course title
    
      const handleOpenPopup = (courseTitle: string) => { // Function to open the popup
        setSelectedCourseTitle(courseTitle); // Set the selected course title
        setIsPopupOpen(true); // Open the popup
      };
    
      const handleClosePopup = () => { // Function to close the popup
        setIsPopupOpen(false); // Close the popup
        setSelectedCourseTitle(""); // Clear selected course when closing
      };

  const faqContent = [
    {
      title: "Introduction to C/C++: Why are C and C++ still relevant?",
      answer:
        "C and C++ are foundational programming languages known for their performance, control over hardware, and efficiency. They are crucial for system programming, operating systems, embedded systems, game development, high-performance computing, and competitive programming. Mastering them provides a deep understanding of how software interacts with hardware.",
    },
    {
      title: "Core C Concepts: What will I learn in basic C programming?",
      answer:
        "In Core C, you'll learn fundamental concepts such as variables, data types, operators, conditional statements (if/else, switch), loops (for, while, do-while), functions, arrays, pointers, strings, structures, unions, and file I/O. This forms the bedrock for understanding computer memory and system-level programming.",
    },
    {
      title: "Core C++ Concepts: How does C++ build upon C?",
      answer:
        "C++ is an extension of C with additional features like classes, objects, inheritance, polymorphism, templates, and exception handling, making it an object-oriented language. You'll learn how C++ supports both procedural and object-oriented programming paradigms, enabling more complex and scalable applications.",
    },
    {
      title: "Object-Oriented Programming (OOP) in C++: What are its principles?",
      answer:
        "OOP in C++ focuses on Encapsulation (data hiding), Inheritance (code reusability), Polymorphism (run-time flexibility), and Abstraction (simplifying complexity). You'll learn how to design and implement robust, modular, and extensible software using these core OOP principles.",
    },
    {
      title: "Data Structures & Algorithms (DSA): Why is DSA important in C/C++?",
      answer:
        "DSA is fundamental for writing efficient and optimized code. In this course, you'll learn various data structures like arrays, linked lists, stacks, queues, trees, graphs, and algorithms for sorting, searching, and problem-solving. Mastering DSA in C/C++ is crucial for competitive programming and tech interviews.",
    },
    {
      title: "Pointers and Memory Management: How are they handled in C/C++?",
      answer:
        "C and C++ provide direct memory management capabilities using pointers. You'll learn about pointer declaration, arithmetic, dynamic memory allocation (`malloc`/`calloc`/`realloc`/`free` in C, `new`/`delete` in C++), and common pitfalls like memory leaks and dangling pointers. This low-level control is a powerful feature.",
    },
    {
      title: "Competitive Programming Prep: Does the course help with competitive programming?",
      answer:
        "Yes, our course provides a strong foundation for competitive programming. With a focus on DSA, time and space complexity analysis, and advanced problem-solving techniques using C++, you will be well-equipped to tackle challenges on platforms like Codeforces, LeetCode, and HackerRank.",
    },
    {
      title: "How does the C/C++ Certification process work?",
      answer:
        "Upon successfully completing all course modules, practical coding assignments, and a final project assessment, you will be awarded a **C/C++ Programming Certification** from Nadezhda Venture. This certificate validates your proficiency in C/C++ and DSA, demonstrating your readiness for technical roles.",
    },
    {
      title: "Is placement assistance provided after certification?",
      answer:
        "Absolutely. We offer dedicated placement support, including resume building, mock interview preparation (with a focus on DSA and core programming), and connecting you with our network of hiring companies for roles requiring strong C/C++ skills in Noida and nationwide.",
    },
    {
      title: "What career paths can I pursue with C/C++ Certification?",
      answer:
        "A C/C++ certification can lead to roles such as Software Engineer (often in system-level or performance-critical areas), Embedded Systems Engineer, Game Developer, Quantitative Developer, High-Frequency Trading Developer, and roles in competitive programming and algorithm development.",
    },
  ];


  const curriculumContent = [
    {
      title: "Module 1: C Language Fundamentals",
      description: "Introduction to C, data types, variables, operators, conditional statements (if-else, switch), loops (for, while, do-while), functions, arrays, and strings. Basic input/output operations.",
    },
    {
      title: "Module 2: Pointers and Memory Management in C",
      description: "Understanding pointers, pointer arithmetic, dynamic memory allocation (malloc, calloc, realloc, free), and working with arrays and functions using pointers. Deeper dive into memory layout.",
    },
    {
      title: "Module 3: Structures, Unions, and File I/O in C",
      description: "Concepts of structures and unions, nested structures, pointers to structures. Reading from and writing to files using file pointers and various file modes. Command-line arguments.",
    },
    {
      title: "Module 4: Introduction to C++ & OOP Concepts",
      description: "Transition from C to C++, basic C++ syntax, namespaces, references. Introduction to Object-Oriented Programming (OOP): classes, objects, data abstraction, encapsulation.",
    },
    {
      title: "Module 5: OOP Pillars - Inheritance & Polymorphism",
      description: "Detailed study of inheritance (single, multiple, multilevel, hierarchical, hybrid), access specifiers. Polymorphism (function overloading, operator overloading, virtual functions, abstract classes).",
    },
    {
      title: "Module 6: Constructors, Destructors & Special Functions",
      description: "Understanding constructors (default, parameterized, copy), destructor. Friend functions and classes, static members, `this` pointer.",
    },
    {
      title: "Module 7: Templates and Exception Handling",
      description: "Generic programming using function templates and class templates. Handling runtime errors gracefully using try-catch blocks and custom exceptions.",
    },
    {
      title: "Module 8: Standard Template Library (STL)",
      description: "Comprehensive study of STL components: containers (vectors, lists, maps, sets), algorithms (sort, find), and iterators. Practical applications of STL for efficient coding.",
    },
    {
      title: "Module 9: Data Structures & Algorithms with C++",
      description: "Implementation of fundamental data structures (stacks, queues, linked lists, trees, graphs) and common algorithms (searching, sorting, greedy, dynamic programming) using C++.",
    },
    {
      title: "Module 10: Advanced Topics & Project Work",
      description: "Introduction to system programming concepts, competitive programming strategies, debugging techniques. Comprehensive capstone project integrating learned concepts to solve a real-world problem or competitive programming challenge.",
    },
  ];

  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex(
        (prevIndex) => (prevIndex + 1) % testimonials.length
      );
    }, 5000); // Auto-slide every 5 seconds

    return () => clearInterval(autoScrollInterval); // Clean up the interval
  }, [testimonials.length]);

  const goToPrevTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const goToNextTestimonial = () => {
    setCurrentTestimonialIndex((prevIndex) =>
      (prevIndex + 1) % testimonials.length
    );
  };

  const getVisibleTestimonialsCount = () => {
    if (window.innerWidth >= 1024) return 3; // lg breakpoint
    if (window.innerWidth >= 768) return 2;  // md breakpoint
    return 1; // default for small screens
  };

  const [visibleCount, setVisibleCount] = useState(getVisibleTestimonialsCount());

  useEffect(() => {
    const handleResize = () => {
      setVisibleCount(getVisibleTestimonialsCount());
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startIndex = currentTestimonialIndex;
  const currentVisibleTestimonials = [];

  // Logic to handle showing multiple cards and wrapping around
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(testimonials[(startIndex + i) % testimonials.length]);
  }


  return (
    <div className="bg-white text-gray-800">
      <Helmet>
        <title>C/C++ Training in Noida | Expert C/C++ Course with Certification</title>
        <meta
          name="description"
          content="Master C/C++ with our comprehensive training in Noida. Learn system programming, game development, DSA with hands-on projects and placement support."
        />
        <meta
          name="keywords"
          content="C/C++ training Noida, C++ course, C++ certification, C++ coaching Noida, Data Structures and Algorithms, Competitive Programming, System Programming, Game Development"
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden text-white h-[400px] bg-gray-900 flex items-center">
        <img
          src="https://plus.unsplash.com/premium_photo-1673688152102-b24caa6e8725?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1yZWxhdGVkfDE2fHx8ZW58MHx8fHx8"
          alt="C/C++ Training Background"
          className="absolute inset-0 w-full h-full object-cover z-0"
        />
         {/* Dark overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-60 z-10" />

       <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-40 flex flex-col md:flex-row items-center md:items-start">
               <div className="md:w-1/2">
                 <motion.h1
                   initial={{ opacity: 0, y: 30 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ duration: 0.8 }}
                   className="text-4xl md:text-5xl font-extrabold leading-tight text-left"
                 >
                   <span className="text-orange-600">C/C++</span> Training in Noida
                 </motion.h1>
       
                 <motion.p
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.3, duration: 0.8 }}
                   className="mt-6 text-lg md:text-xl text-gray-200 text-left"
                 >
                   Join Nadezhda Venture for expert-led C/C++ Training in Noida. Build robust programming skills with hands-on projects and certification support.
                 </motion.p>
       
                 <motion.button
                   initial={{ opacity: 0, y: 20 }}
                   animate={{ opacity: 1, y: 0 }}
                   transition={{ delay: 0.6, duration: 0.8 }}
                   className="mt-8 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-full shadow-lg transition duration-300 ease-in-out"
                 >
                   <button
                    // className="bg-orange-600 hover:bg-orange-700 px-6 py-3 rounded-lg font-semibold transition text-white"
                    onClick={() => handleOpenPopup("C/C++ Certification Course")} // Pass the course title
                  >
                    Enroll Now !
                  </button>
                 </motion.button>
               </div>
               </div>
      </section>

      {/* Why C/C++ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Why Learn C/C++?</h2>
          <p className="text-lg text-gray-700 mb-8 max-w-3xl mx-auto">
            C and C++ are foundational languages for high-performance computing, crucial for system programming, game development, and competitive programming. They offer unparalleled control over hardware and memory, making them indispensable for critical applications.
          </p>
          <div className="grid md:grid-cols-3 gap-8 mt-10">
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">High Performance</h3>
              <p className="text-gray-600">Ideal for performance-critical applications, system programming, and embedded systems.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">Foundational Skills</h3>
              <p className="text-gray-600">Deep understanding of memory management, data structures, and algorithms.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300">
              <h3 className="font-semibold text-xl text-blue-700 mb-3">Versatile Applications</h3>
              <p className="text-gray-600">Used in game engines, operating systems, compilers, and competitive programming.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Course Highlights Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Course Highlights</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '🚀', title: 'Strong Foundational Concepts', desc: 'Build a robust understanding of programming logic from basics.' },
              { icon: '💡', title: 'Hands-on DSA & OOP', desc: 'Master Data Structures, Algorithms, and Object-Oriented Programming with practical problems.' },
              { icon: '👨‍🏫', title: 'Experienced Industry Trainers', desc: 'Learn from experts with extensive experience in C/C++ development.' },
              { icon: '🎓', title: 'Globally Recognized Certification', desc: 'Enhance your profile with our industry-aligned course certificate.' },
              { icon: '🤝', title: 'Dedicated Placement Assistance', desc: 'Receive comprehensive support for resume building, mock interviews, and job placements.' },
              { icon: '🔄', title: 'Flexible Learning Options', desc: 'Choose between immersive offline classroom or interactive online sessions.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-blue-50 p-6 rounded-lg shadow-md flex items-start space-x-4"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-4xl">{item.icon}</div>
                <div>
                  <h3 className="font-semibold text-xl text-blue-800 mb-2">{item.title}</h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Curriculum Overview Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-blue-900">Detailed Curriculum Overview</h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {module.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Tools & Technologies You'll Master</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            Our C/C++ training ensures you gain proficiency in the essential tools and libraries used in various C/C++ development domains.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center justify-center">
            {[
              { name: 'VS Code', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vscode/vscode-original.svg' },
              { name: 'GCC/G++', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/gcc/gcc-original.svg' },
              { name: 'Visual Studio', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/visualstudio/visualstudio-plain.svg' },
              { name: 'Data Structures', logo: 'https://cdn-icons-png.flaticon.com/512/12297/12297565.png' }, // Placeholder, find a better conceptual logo
              { name: 'STL', logo: 'https://img.freepik.com/premium-vector/stl-file-icon-vector-image-can-be-used-additive-maufacturing_120816-375360.jpg' }, // Placeholder, find a better logo
              { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg' },
            ].map((tool, index) => (
              <motion.div
                key={index}
                className="flex flex-col items-center p-4 bg-gray-100 rounded-lg shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <img src={tool.logo} alt={tool.name} className="h-12 mb-2" />
                <p className="text-gray-700 font-medium text-sm">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-blue-900">Exciting Career Opportunities</h2>
          <p className="text-lg text-gray-700 mb-10 max-w-3xl mx-auto">
            A C/C++ certification from Appwars Technologies can unlock a wide array of high-demand job roles in performance-critical industries.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: '💻', title: 'Software Engineer', desc: 'Develop high-performance software applications and systems.' },
              { icon: '🎮', title: 'Game Developer', desc: 'Build game engines and complex game logic for various platforms.' },
              { icon: '🔌', title: 'Embedded Systems Engineer', desc: 'Design and program software for microcontrollers and embedded devices.' },
              { icon: '📊', title: 'Quantitative Developer', desc: 'Work in finance building high-frequency trading systems.' },
              { icon: '🏆', title: 'Competitive Programmer', desc: 'Excel in algorithmic problem-solving and coding contests.' },
              { icon: '⚙️', title: 'Systems Programmer', desc: 'Develop operating systems, compilers, and network protocols.' },
            ].map((role, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 flex flex-col items-center text-center"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-5xl mb-4">{role.icon}</div>
                <h3 className="font-semibold text-xl text-blue-800 mb-2">{role.title}</h3>
                <p className="text-gray-700">{role.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* Course Content / FAQ Section */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-3 gap-10">
        <div className="md:col-span-2">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center md:text-left">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((section, idx) => (
              <div
                key={idx}
                className="border border-gray-200 rounded-lg shadow-sm overflow-hidden bg-white"
              >
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-50 hover:bg-gray-100 transition duration-200 ease-in-out flex justify-between items-center">
                    {section.title}
                    <span className="transform transition-transform duration-200 group-open:rotate-180">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </span>
                  </summary>
                  <div className="px-6 py-4 text-gray-700 leading-relaxed border-t border-gray-200">
                    <p>{section.answer}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>

        {/* Enquiry Form */}
        <div className="md:col-span-1 pt-20">
          <div className="bg-blue-300 rounded-lg p-6 shadow-xl">
            <h3 className="text-2xl font-bold mb-4 text-center text-blue-800">Enquire Now</h3>
            <p className="text-center text-gray-600 mb-6">Fill out the form below to get more details about our C/C++ training program.</p>
           <form
  className="space-y-4"
  onSubmit={async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = {
      firstName: form.fullName.value.split(" ")[0] || "",
      lastName: form.fullName.value.split(" ")[1] || "",
      businessEmail: form.email.value,
      phone: form.phone.value,
      companyName: "N/A",
      interests: ["C/C++"],
      howDidYouFindUs: "Website",
      subscribeNewsletter: false,
      message: "C/C++ Enquiry",
      courseOfInterest: form.course.value,
      collegeName: form.college.value,
    };

    try {
      const response = await fetch("http://localhost:8080/api/submit-form", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitMessage("✅ Thank you! Your enquiry has been submitted.");
        form.reset();
      } else {
        setSubmitMessage("❌ Submission failed. Please try again.");
      }
    } catch (err) {
      setSubmitMessage("❌ An error occurred. Please try again later.");
    }

    setTimeout(() => setSubmitMessage(null), 2000); // Hide after 2 sec
  }}
>
  <input
    type="text"
    name="fullName"
    placeholder="Your Name"
    required
    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
  <input
    type="text"
    name="course"
    placeholder="Course of Interest (e.g., C/C++ Training)"
    required
    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
  <input
    type="text"
    name="college"
    placeholder="College/University Name"
    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
  <input
    type="email"
    name="email"
    placeholder="Your Email"
    required
    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
  <input
    type="tel"
    name="phone"
    placeholder="Phone Number"
    required
    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
  />
  <button
    type="submit"
    className="w-full bg-blue-800 hover:bg-blue-700 text-white py-3 rounded-md font-semibold transition duration-300 ease-in-out shadow-md hover:shadow-lg"
  >
    JOIN THE PROGRAM
  </button>

  {submitMessage && (
    <p className="text-sm text-center mt-2 text-green-700 bg-green-100 p-2 rounded-md transition-opacity duration-500 ease-in-out">
      {submitMessage}
    </p>
  )}
</form>
          </div>
        </div>
      </section>

      {/* Testimonial Section - No Horizontal Scrollbar */}
      <section className="bg-blue-50 py-16">
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-blue-900">Our Students Say</h2>

          <div className="relative overflow-hidden w-full mx-auto">
            {/* Left Scroll Button */}
            <button
              onClick={goToPrevTestimonial}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Previous testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {/* Testimonial Cards Container */}
            <div className="flex justify-center items-stretch space-x-6 md:space-x-8 lg:space-x-10">
              <AnimatePresence mode="wait" initial={false}>
                {currentVisibleTestimonials.map((testimonial, idx) => (
                  <motion.div
                    key={testimonial.id}
                    initial={{ opacity: 0, x: idx * 100 }} // Initial position based on index in visible set
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                    className="flex-none w-full md:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-2rem)] p-2"
                  >
                    <div className="bg-white p-6 rounded-lg shadow-md border border-gray-100 flex flex-col h-full text-left">
                      <div className="flex items-center mb-4">
                        <img
                          src={testimonial.profileImg}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover mr-4 border-2 border-blue-200"
                        />
                        <div>
                          <p className="font-bold text-gray-900 text-lg">
                            {testimonial.name}
                          </p>
                          <p className="text-gray-500 text-sm">{testimonial.date}</p>
                        </div>
                      </div>
                      <div className="flex items-center mb-3">
                        <StarRating rating={testimonial.rating} />
                        {testimonial&& <VerifiedBadge />}
                      </div>
                      <p className="text-gray-700 text-base leading-relaxed flex-grow line-clamp-4">
                        {testimonial.review}
                      </p>
                      <a href="#" className="text-blue-600 hover:underline mt-4 text-sm text-right">
                        Read more
                      </a>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {/* Right Scroll Button */}
            <button
              onClick={goToNextTestimonial}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors duration-300 hidden md:block"
              aria-label="Next testimonial"
            >
              <svg
                className="w-6 h-6 text-gray-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                className={`h-3 w-3 rounded-full ${
                  // Logic to highlight dots based on the current set of visible testimonials
                  idx >= currentTestimonialIndex && idx < currentTestimonialIndex + visibleCount
                    ? "bg-blue-600"
                    : "bg-gray-300"
                } transition-colors duration-300`}
                onClick={() => setCurrentTestimonialIndex(idx)}
                aria-label={`Go to testimonial group ${idx + 1}`}
              ></button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Conditionally render the popup */}
            {isPopupOpen && (
              <JoinNowFormPopup
                onClose={handleClosePopup}
                courseTitle={selectedCourseTitle}
              />
            )}
    </div>
  );
};

export default CPlusCourseInNoida;